import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { MatSidenavModule } from '@angular/material/sidenav';
import { LayoutModule } from '@angular/cdk/layout';
import { ChatBoxComponent } from './chat-box/chat-box.component';
import { ChatProfileBoxComponent } from './chat-profile-box/chat-profile-box.component';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AuthConfig } from 'angular-oauth2-oidc';
import { SpyglassChatComponent } from './spyglass-chat/spyglass-chat.component';
import { ChatComponent } from './chat/chat.component';
import { ChatAuthorshipFieldComponent } from './chat-authorship-field/chat-authorship-field.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ConfigService } from './config.service';
import { MsalAccessService } from '../auth/msal-service';
import { OauthService } from '../auth/oauth2-service';
import { firstValueFrom, timer } from 'rxjs';
import { switchMap, filter, first, tap } from 'rxjs/operators';
import { ConfigResponse } from '../spyglass-search/shared/config-response';
import { AuthInterceptorService } from '../auth/auth-interceptor.service';
import { CookieConsentService } from '../shared/cookie-consent.service';
import { ChatInfoMenuComponent } from './chat-info-menu/chat-info-menu.component';
import { ChatHistoryComponent } from './chat-history/chat-history.component';

export function initializeApp(
  configService: ConfigService,
  msalService: MsalAccessService,
  oauthService: OauthService,
) {
  return () => {
    const pollingInterval = 1000; // Interval between polling attempts (in milliseconds)

    return firstValueFrom(
      timer(0, pollingInterval).pipe(
        tap(() => console.log('Polling for config...')),
        switchMap(() => configService.getConfig('default')),
        tap((configResponse) => console.log('Config response:', configResponse)),
        filter(
          (configResponse): configResponse is ConfigResponse =>
            configResponse !== null && configResponse.msalConfig !== null
        ),
        first() // Ensures that the Observable completes after the first matching value
      )
    )
    .then((configResponse) => {
      if (!configResponse) {
        console.error('Config response is undefined.');
        return;
      }
    
      const msalConf = configResponse.msalConfig;
      const shouldUseTokenFromOauth = configResponse.shouldUseTokenFromOauth ?? false;
      const microsoftGalaxyOauth = configResponse.microsoftGalaxyOauth;
      const providers = configResponse.oidcConfig ?? {};
      const pkceProvider: any = Object
        .values(providers)
        .find((p: any) => p.active && p.issuer);
    
      if (pkceProvider) {
        const authCfg: AuthConfig = {
          issuer:     pkceProvider.issuer,
          clientId:   pkceProvider.clientId,
          redirectUri: pkceProvider.redirectUri,
          scope:      pkceProvider.scope ?? 'openid email profile',
          responseType: pkceProvider.responseType ?? 'code',
          requireHttps: pkceProvider.requireHttps ?? true,
          strictDiscoveryDocumentValidation:
                      pkceProvider.strictDiscoveryDocumentValidation ?? false,
          skipIssuerCheck: pkceProvider.skipIssuerCheck ?? false
        };
    
        oauthService.loadConfig(authCfg);
      }
    
      msalService.loadConfig(msalConf, shouldUseTokenFromOauth, microsoftGalaxyOauth);
    });    
  };
}

@NgModule({
  declarations: [
    ChatBoxComponent,
    ChatProfileBoxComponent,
    SpyglassChatComponent,
    ChatComponent,
    ChatAuthorshipFieldComponent,
    ChatInfoMenuComponent,
    ChatHistoryComponent
  ],
  exports: [
    ChatProfileBoxComponent,
    SpyglassChatComponent,
    CommonModule,
    BrowserModule,
    ReactiveFormsModule,
    FormsModule,
    ChatInfoMenuComponent,
    ChatHistoryComponent
  ],
  imports: [
    CommonModule,
    BrowserModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    NoopAnimationsModule,
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatMenuModule,
    MatInputModule,
    MatButtonModule,
    MatGridListModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    RouterModule,
    InfiniteScrollDirective,
    MatSidenavModule,
    LayoutModule
  ],
  providers: [
    ConfigService,
    MsalAccessService,
    OauthService,
    CookieConsentService,
    provideAppInitializer(() => {
      const initializerFn = initializeApp(
        inject(ConfigService),
        inject(MsalAccessService),
        inject(OauthService)
      );
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class SpyglassChatModule {}
